import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import { connect } from 'react-redux';
import parse from 'url-parse';
import parseISO from 'date-fns/parseISO';
import { actions, constants } from 'app-webtech-core';

import PlayIcon from '@material-ui/icons/PlayCircleFilled';

import { changeUrlSlug as changeChannelUrlSlug } from '../../guide/actions';

const { GUIDE_LIST_ITEM_KIND } = constants.guide;

// CSS ENTRANCE ANIMATION CONFIGURATION

// This is most easily tweaked using the Chrome Animation Inspector:
// https://developers.google.com/web/tools/chrome-devtools/inspect-styles/animations

// Timings are in seconds.
// Increase initial delay to something like 6 in dev for ease of testing,
const INTRO_ANIMATION_DELAY = 1.5;
const INTRO_ANIMATION_DURATION = 1.75;

const PURPLE_SHADE = 'rgb(94, 0, 255)';

// Pixels. 'px' string interpolated where used to allow arithmetic.
const CTA_HEIGHT_MOBILE = 32;
const CTA_HEIGHT_DESKTOP = 46;
const CTA_MIN_WIDTH_MOBILE = 150;
const CTA_MIN_WIDTH_DESKTOP = 250;
const PURPLE_SHADOW_WIDTH_MOBILE = 3;
const PURPLE_SHADOW_WIDTH_DESKTOP = 5;
const WHITE_LOZENGE_LEFT_PADDING_MOBILE = 0;
const WHITE_LOZENGE_LEFT_PADDING_DESKTOP = 2;

const OverlayLink = styled.a`
  cursor: pointer;
  display: block;
  width: 100%;
  position: absolute;
  left: 0;
  padding: 0;
  text-decoration: none;
  z-index: 5;
  top: ${({ isMobile, internalFullscreen }) => (isMobile || internalFullscreen ? '0' : '60px')};
  height: ${({ isMobile, internalFullscreen }) =>
    isMobile || internalFullscreen ? '100%' : 'calc(100% - 60px)'};
  user-select: none;

  &::focus {
    outline: none;
  }

  &:moz-focusring {
    outline: none;
  }
`;

const CTA = styled.div`
  position: absolute;
  top: 16px;
  right: 0;
`;

const ButtonWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const CTAButton = styled.div`
  align-items: center;
  color: black;
  display: flex;
  font-family: 'Pluto Sans SemiBold';
  font-weight: 600;
  flex-direction: row;
  overflow: hidden;
  opacity: 0;
  position: relative;
  text-align: center;
  white-space: nowrap;

  font-size: ${({ isMobile }) => (isMobile ? '12px' : '16px')};
  height: ${({ isMobile }) => (isMobile ? `${CTA_HEIGHT_MOBILE}px` : `${CTA_HEIGHT_DESKTOP}px`)};
`;

const CTAButtonPlaceholder = styled(CTAButton)`
  padding: ${({ isMobile }) =>
    isMobile
      ? `0 6px 0px ${
          CTA_HEIGHT_MOBILE / 2 + PURPLE_SHADOW_WIDTH_MOBILE + WHITE_LOZENGE_LEFT_PADDING_MOBILE
        }px`
      : `0 12px 1px ${
          CTA_HEIGHT_DESKTOP / 2 + PURPLE_SHADOW_WIDTH_DESKTOP + WHITE_LOZENGE_LEFT_PADDING_DESKTOP
        }px`};
`;

const ctaButtonKeyframes = ({ isMobile }) => keyframes`
  0% {
    opacity: 0;
  }
  33% {
    opacity: 0;
  }
  33.01% {
    opacity: 1; // Render behind Orb once Orb has reached max height in front of it.
  }
  50% {
    width: ${isMobile ? `${CTA_HEIGHT_MOBILE / 2}px` : `${CTA_HEIGHT_DESKTOP / 2}px`};
  }
  90% {
    width: 100%;
    opacity: 1;
  }
  // Re-state values so that 'forwards' declaration works cross-browser.
  100% {
    width: 100%;
    opacity: 1;
  }
`;

// The visible version of the CTA has the animation on it:
const CTAButtonAnimated = styled(CTAButton)`
  animation: ${(props) => css`
    ${ctaButtonKeyframes(
      props,
    )} ${INTRO_ANIMATION_DURATION}s linear ${INTRO_ANIMATION_DELAY}s forwards;
  `};
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ isMobile }) =>
    isMobile ? `${CTA_HEIGHT_MOBILE / 2}px` : `${CTA_HEIGHT_DESKTOP / 2}px`};
`;

const AnimatedLozenge = styled.div`
  border-radius: ${({ isMobile }) =>
    isMobile
      ? `${CTA_HEIGHT_MOBILE / 2}px 0 0 ${CTA_HEIGHT_MOBILE / 2}px`
      : `${CTA_HEIGHT_DESKTOP / 2}px 0 0 ${CTA_HEIGHT_DESKTOP / 2}px`};
  height: 100%;
  width: 100%;
`;

const PurpleAnimatedLozenge = styled(AnimatedLozenge)`
  background: ${PURPLE_SHADE};
`;

const WhiteAnimatedLozenge = styled(AnimatedLozenge)`
  text-align: left;
  white-space: nowrap;
`;

const LozengeSegment = styled.div`
  background: white;
  display: inline-block;
  height: 100%;
  vertical-align: top;
`;

const leftLozengeKeyframes = ({ isMobile }) => keyframes`
  0% {
    transform: translateX(0);
  }
  77% {
    transform: translateX(0);
  }
  // Finish early.
  92% {
    transform: ${
      isMobile
        ? `translateX(${PURPLE_SHADOW_WIDTH_MOBILE}px)`
        : `translateX(${PURPLE_SHADOW_WIDTH_DESKTOP}px)`
    };
  }
  100% {
    transform: ${
      isMobile
        ? `translateX(${PURPLE_SHADOW_WIDTH_MOBILE}px)`
        : `translateX(${PURPLE_SHADOW_WIDTH_DESKTOP}px)`
    };
  }
`;

const LeftWhiteAnimatedLozenge = styled(LozengeSegment)`
  animation: ${(props) => css`
    ${leftLozengeKeyframes(
      props,
    )} ${INTRO_ANIMATION_DURATION}s ease ${INTRO_ANIMATION_DELAY}s forwards;
  `};
  border-radius: ${({ isMobile }) =>
    isMobile
      ? `${CTA_HEIGHT_MOBILE / 2}px 0 0 ${CTA_HEIGHT_MOBILE / 2}px`
      : `${CTA_HEIGHT_DESKTOP / 2}px 0 0 ${CTA_HEIGHT_DESKTOP / 2}px`};
  width: ${({ isMobile }) =>
    isMobile
      ? `${CTA_HEIGHT_MOBILE / 2 + PURPLE_SHADOW_WIDTH_MOBILE}px`
      : `${CTA_HEIGHT_DESKTOP / 2 + PURPLE_SHADOW_WIDTH_DESKTOP}px`};
  transform: translateX(0);
`;

const RightWhiteAnimatedLozenge = styled(LozengeSegment)`
  width: ${({ isMobile }) =>
    isMobile
      ? `calc(100% - ${CTA_HEIGHT_MOBILE / 2 + PURPLE_SHADOW_WIDTH_MOBILE}px)`
      : `calc(100% - ${CTA_HEIGHT_DESKTOP / 2 + PURPLE_SHADOW_WIDTH_DESKTOP}px)`};
  transform: translateX(0); // Promote so it overpaints.
`;

const RightWhiteAnimatedLozengeInner = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  height: 100%;
  padding: ${({ isMobile }) =>
    isMobile
      ? `0 6px 0px ${WHITE_LOZENGE_LEFT_PADDING_MOBILE}px`
      : `0 12px 1px ${WHITE_LOZENGE_LEFT_PADDING_DESKTOP}px`};
  white-space: nowrap;
`;

const ButtonTextSpan = styled.span`
  min-width: ${({ isMobile }) => (isMobile ? '84px' : '184px')};
  padding-right: 0.25em;
`;

const IconWrapper = styled.span`
  height: ${({ isMobile }) => (isMobile ? '22px' : '24px')};
`;

const iconStyle = {
  color: PURPLE_SHADE,
};

// The "Orb" is the first thing to appear visually at the beginning of the CTA.
// It starts as a circle, then expands to width of the text box,
// then slides to the right to reveal the button behind it.
const orbKeyframes = ({ isMobile }) => keyframes`
  0% {
    transform: scale(0);
  }
  33% {
    transform: scale(1);
    width: ${isMobile ? `${CTA_HEIGHT_MOBILE}px` : `${CTA_HEIGHT_DESKTOP}px`};
  }
  50% {
    transform: scale(1);
    width: ${isMobile ? `${CTA_HEIGHT_MOBILE}px` : `${CTA_HEIGHT_DESKTOP}px`};
  }
  66% {
    transform: translateX(0);
    width: 115%; // Can't use calc() reliably in keyframes so just overshoot slightly.
  }
  100% {
    transform: translateX(100%);
    width: 115%;
  }
`;

const Orb = styled.div`
  animation: ${(props) => css`
    ${orbKeyframes(props)} ${INTRO_ANIMATION_DURATION}s linear ${INTRO_ANIMATION_DELAY}s both;
  `};
  background: ${PURPLE_SHADE};
  border-radius: ${({ isMobile }) =>
    isMobile ? `${CTA_HEIGHT_MOBILE / 2}px` : `${CTA_HEIGHT_DESKTOP / 2}px`};
  height: 100%;
  width: ${({ isMobile }) => (isMobile ? `${CTA_HEIGHT_MOBILE}px` : `${CTA_HEIGHT_DESKTOP}px`)};
  position: absolute;
  top: 0;
  left: 0;
`;

// TODO find a better solution for increasing specificity without adding a
// new package or messing with the process.env :(
// https://styled-components.com/docs/advanced#issues-with-specificity
const ResponsivePlayIcon = styled(PlayIcon)`
  font-size: ${({ isMobile }) => (isMobile ? '22px !important' : '24px')};
`;

class PlayerCTAComponent extends PureComponent {
  static propTypes = {
    adId: PropTypes.string.isRequired,
    burnedInButton: PropTypes.bool.isRequired,
    buttonText: PropTypes.string.isRequired,
    endDateTime: PropTypes.string,
    deviceId: PropTypes.string.isRequired,
    internalFullscreen: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool.isRequired,
    startDateTime: PropTypes.string,
    targetUrl: PropTypes.string.isRequired,
    changeChannelUrlSlug: PropTypes.func.isRequired,
    currentPlayingChannelId: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.changeChannel = this.changeChannel.bind(this);
    this.state = { clicked: '' };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    // When an internal ad is clicked on, we immediately hide the CTA, and don't wait for the
    // new channel fragments to load in the player, in order to keep the UI feeling responsive.
    // But whenever a new adId is rendered, we want to reset the state to unclicked.
    return nextProps.adId === prevState.clicked ? {} : { clicked: '' };
  }

  changeChannel(e) {
    const { adId, channelsList, deviceId, linearChannelSlugs, targetUrl } = this.props;

    const isExternalLink = targetUrl.substring(0, 17) !== 'https://pluto.tv/';

    if (isExternalLink) {
      return;
    }
    const parsedRelUrl = parse(targetUrl);
    const splitUrl = parsedRelUrl.pathname.split('/');
    const isLiveTV = splitUrl[1] === 'live-tv';

    if (!isLiveTV) {
      return; // Currently only changing channels to live TV. Can handle VOD later.
    }

    e.preventDefault();
    e.stopPropagation();
    this.setState({ clicked: adId }); // Hides the ad
    const liveTvSlug = splitUrl[2];
    const targetChannelId = linearChannelSlugs[liveTvSlug].channel;
    const targetChannelIndex = channelsList.findIndex(
      (c) => c.kind === GUIDE_LIST_ITEM_KIND.CHANNEL && c.id === targetChannelId,
    );

    this.props.changeChannelUrlSlug(liveTvSlug);
    this.props.playChannelAtIndex({
      index: targetChannelIndex,
      setSelected: true,
    });
    // Send beacon to Phoenix.
    // Question: it looks like playChannelAtIndex also fires a channel change beacon.
    // Is it a desired scenario that a second channelChange event be fired?
    this.props.adClickChannelChange({
      targetChannelId,
      utmSource: 'plutotv',
      utmMedium: 'web',
      utmCampaign: adId,
      utmTerm: '',
      utmContent: deviceId,
    });
  }

  render() {
    const {
      adId,
      burnedInButton,
      buttonText,
      deviceId,
      endDateTime,
      internalFullscreen,
      isMobile,
      startDateTime,
      targetUrl,
    } = this.props;

    const { clicked } = this.state;
    if (clicked) {
      return null;
    }

    // Ad is assumed to be always on if the optional datetime fields are missing.
    const currentDateTime = new Date();
    const adIsCurrentlyScheduled = !(
      (startDateTime && currentDateTime < parseISO(startDateTime)) ||
      (endDateTime && currentDateTime > parseISO(endDateTime))
    );
    if (!adIsCurrentlyScheduled) {
      return null;
    }

    // Internal urls should just change the channel without relaunch.
    // External urls should open in a new tab.
    const isExternalLink = targetUrl.substring(0, 17) !== 'https://pluto.tv/';

    // Append tracking UTM params to internal links only.
    const targetUrlWithParams = isExternalLink
      ? targetUrl
      : `${targetUrl}?utm_source=plutotv&utm_medium=web&utm_campaign=${adId}&utm_term=&utm_content=${deviceId}`;

    return (
      <OverlayLink
        {...{
          href: targetUrlWithParams,
          internalFullscreen,
          isMobile,
          ...(isExternalLink && { target: '_blank' }),
          ...(isExternalLink && { rel: 'noopener noreferrer' }),
          onClick: this.changeChannel,
        }}
      >
        {!burnedInButton && (
          <CTA>
            <ButtonWrapper>
              {/* The two ctas have the same content.
              The first is for sizing and is transparent. */}
              <CTAButtonPlaceholder isMobile={isMobile}>
                <ButtonTextSpan isMobile={isMobile}>{buttonText}</ButtonTextSpan>
                <IconWrapper isMobile={isMobile} style={iconStyle}>
                  <ResponsivePlayIcon isMobile={isMobile} />
                </IconWrapper>
              </CTAButtonPlaceholder>
              <CTAButtonAnimated isMobile={isMobile}>
                <PurpleAnimatedLozenge>
                  <WhiteAnimatedLozenge>
                    <LeftWhiteAnimatedLozenge isMobile={isMobile} />
                    <RightWhiteAnimatedLozenge isMobile={isMobile}>
                      <RightWhiteAnimatedLozengeInner isMobile={isMobile}>
                        <ButtonTextSpan isMobile={isMobile}>{buttonText}</ButtonTextSpan>
                        <IconWrapper isMobile={isMobile} style={iconStyle}>
                          <ResponsivePlayIcon isMobile={isMobile} />
                        </IconWrapper>
                      </RightWhiteAnimatedLozengeInner>
                    </RightWhiteAnimatedLozenge>
                  </WhiteAnimatedLozenge>
                </PurpleAnimatedLozenge>
              </CTAButtonAnimated>
              <Orb isMobile={isMobile} />
            </ButtonWrapper>
          </CTA>
        )}
      </OverlayLink>
    );
  }
}

const mapStateToProps = (state) => ({
  channelsList: state.guide.list,
  deviceId: state.device.ID,
  linearChannelSlugs: state.linearChannels.entities.linearChannelSlugs,
});

const mapDispatchToProps = {
  changeChannelUrlSlug,
  playChannelAtIndex: actions.guide.playChannelAtIndex,
  adClickChannelChange: actions.guide.adClickChannelChange,
};

export const PlayerCTA = connect(mapStateToProps, mapDispatchToProps)(PlayerCTAComponent);
